"use client";
import type { ReactNode } from "react";
import { ArrowDownUpIcon, FilterIcon, XIcon } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RECIPE_TYPE_LIST, RECIPE_TYPE_MAP } from "@/lib/recipeType";
import type { RecipeType } from "@/lib/types";
import {
  CALORIE_FILTER_OPTIONS,
  getCaloriesFilterValue,
  RecipeSort,
  type CalorieRange,
  type RecipeTypeFilter,
} from "./recipe-search";

const recipeTypeFilterSchema = z.enum(["all", ...RECIPE_TYPE_LIST]);
const recipeSortSchema = z.enum(RecipeSort);
export function RecipeSearchControls({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2 pb-3 md:flex-row">{children}</div>;
}
export function RecipeSearchActions({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-2 md:flex md:flex-none">
      {children}
    </div>
  );
}
export function RecipeSearchMetadata({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3">{children}</div>;
}
export function RecipeFiltersMenu({
  activeFilterCount,
  children,
}: {
  activeFilterCount: number;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="w-full md:w-auto"
          />
        }
      >
        <FilterIcon aria-hidden /> Filters
        <span className="bg-border mx-0.5 h-4 w-px" aria-hidden />
        <span className="text-muted-foreground tabular-nums">
          <span className="sr-only">Active filters: </span>
          {activeFilterCount}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
export function RecipeTypeFilterGroup({
  value,
  onValueChange,
}: {
  value: RecipeTypeFilter;
  onValueChange: (value: RecipeTypeFilter) => void;
}) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Recipe type</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={value}
        onValueChange={(type) => {
          const result = recipeTypeFilterSchema.safeParse(type);
          if (result.success) onValueChange(result.data);
        }}
      >
        <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
        {RECIPE_TYPE_LIST.map((type) => (
          <DropdownMenuRadioItem key={type} value={type}>
            <RecipeTypeFilterLabel type={type} />
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuGroup>
  );
}
export function RecipeCaloriesFilterGroup({
  value,
  onValueChange,
}: {
  value: CalorieRange;
  onValueChange: (value: CalorieRange) => void;
}) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Calories (kcal/100g)</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={getCaloriesFilterValue(value)}
        onValueChange={(optionValue) => {
          const option = CALORIE_FILTER_OPTIONS.find(
            (calorieOption) => calorieOption.value === optionValue,
          );
          onValueChange({
            caloriesMin: option?.caloriesMin ?? null,
            caloriesMax: option?.caloriesMax ?? null,
          });
        }}
      >
        <DropdownMenuRadioItem value="any">Any</DropdownMenuRadioItem>
        {CALORIE_FILTER_OPTIONS.map((option) => (
          <DropdownMenuRadioItem
            key={option.value}
            value={option.value}
            className="items-start py-2"
          >
            <div>
              <div>{option.label}</div>
              <div className="text-muted-foreground text-xs leading-snug">
                {option.description}
              </div>
            </div>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuGroup>
  );
}
export function RecipeTypeFilterLabel({ type }: { type: RecipeType }) {
  const recipeType = RECIPE_TYPE_MAP[type];
  const Icon = recipeType?.icon;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {Icon ? (
        <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      ) : null}
      <span className="truncate">{recipeType?.label ?? type}</span>
    </span>
  );
}
export function RecipeSortSelect({
  value,
  onValueChange,
}: {
  value: RecipeSort;
  onValueChange: (value: RecipeSort) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(sort) => {
        const result = recipeSortSchema.safeParse(sort);
        if (result.success) onValueChange(result.data);
      }}
    >
      <SelectTrigger
        aria-label="Sort recipes"
        className="justify-center gap-1 px-3 md:w-32"
      >
        <ArrowDownUpIcon className="size-4" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={RecipeSort.NEWEST}>Newest</SelectItem>
        <SelectItem value={RecipeSort.OLDEST}>Oldest</SelectItem>
      </SelectContent>
    </Select>
  );
}
export function ActiveFilterChip({
  children,
  onRemove,
}: {
  children: ReactNode;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="bg-card hover:bg-accent interactive inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium"
    >
      {children}
      <XIcon className="text-muted-foreground size-3" aria-hidden />
    </button>
  );
}
export function SearchResultsSummary({ totalCount }: { totalCount: number }) {
  const resultLabel = totalCount === 1 ? "recipe" : "recipes";
  return (
    <p className="text-muted-foreground text-sm">
      {totalCount.toLocaleString()} {resultLabel} found
    </p>
  );
}
